/**
 * Theming — a tiny persisted preference + a pure resolver, driving a single
 * `data-theme` attribute on `<html>` that `tokens.css` keys its palettes off.
 * `auto` follows `prefers-color-scheme`; `light`/`dark` pin it explicitly.
 */

export type ThemePref = 'auto' | 'light' | 'dark';
export type ThemeId = 'dusk-light' | 'dusk-dark';

const KEY = 'swarasense.theme';

/** Registry of concrete themes tokens.css implements — for a future theme picker. */
export const THEMES: { id: ThemeId; label: string }[] = [
  { id: 'dusk-light', label: 'Dusk (light)' },
  { id: 'dusk-dark', label: 'Dusk (dark)' },
];

/** Pure: pref + system preference → the concrete theme id. Explicit prefs win over system. */
export function resolveTheme(pref: ThemePref, prefersDark: boolean): ThemeId {
  const dark = pref === 'dark' || (pref === 'auto' && prefersDark);
  return dark ? 'dusk-dark' : 'dusk-light';
}

export function getThemePreference(): ThemePref {
  const v = localStorage.getItem(KEY);
  return v === 'light' || v === 'dark' || v === 'auto' ? v : 'auto';
}

/** Fired on `document` whenever the resolved theme id changes, so Canvas/JS colour
 * constants (read via `cssVar`) know to recompute. */
export const THEME_CHANGE_EVENT = 'themechange';

function apply(pref: ThemePref): void {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const theme = resolveTheme(pref, prefersDark);
  document.documentElement.dataset.theme = theme;
  document.dispatchEvent(new CustomEvent<ThemeId>(THEME_CHANGE_EVENT, { detail: theme }));
}

export function setThemePreference(p: ThemePref): void {
  localStorage.setItem(KEY, p);
  apply(p);
}

export function initTheme(): void {
  apply(getThemePreference());
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getThemePreference() === 'auto') apply('auto');
  });
}

/**
 * Reads a CSS custom property off `<html>` — how Canvas/JS colour constants (the
 * ladder tones, the results review contour) stay themed without a literal hex:
 * they call this instead of hardcoding a colour, and re-read it on `THEME_CHANGE_EVENT`.
 */
export function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}
